"use client";

import { useState } from "react";
import { Modal, TextInput, Button, Stack, Title, Text } from "@mantine/core";

const EMAIL_STORAGE_KEY = "ca_lead_email";

function isValidEmail(value: string): boolean {
  const at = value.indexOf("@");
  if (at < 0) return false;
  const dot = value.indexOf(".", at + 1);
  return dot > at + 1 && dot < value.length - 1;
}

export function getStoredLeadEmail(): string | null {
  try {
    return localStorage.getItem(EMAIL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeLeadEmail(email: string): void {
  try {
    localStorage.setItem(EMAIL_STORAGE_KEY, email);
  } catch {
    // ignore
  }
}

type CheckoutEmailStepProps = {
  open: boolean;
  priceDisplay: string;
  loading: boolean;
  onSubmit: (email: string) => void;
  onClose: () => void;
};

export default function CheckoutEmailStep({ open, priceDisplay, loading, onSubmit, onClose }: CheckoutEmailStepProps) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const attemptSubmit = () => {
    if (!isValidEmail(email)) {
      setError("Enter a valid email address");
      return;
    }
    setError(null);
    onSubmit(email);
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      centered
      size="md"
      radius="lg"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      // Page decor/cards sit above Mantine's default modal layer (200).
      zIndex={10000}
      // The fade transition stalls on this page (content stuck at ~30% opacity);
      // open instantly instead.
      transitionProps={{ duration: 0 }}
    >
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={3} fz="md">
            Where should we send your analysis?
          </Title>
          <Text fz="sm" c="dimmed">
            Results land in about 2 minutes. We'll also email a copy so you never lose it.
          </Text>
        </Stack>

        <TextInput
          type="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              attemptSubmit();
            }
          }}
          error={error}
          size="md"
        />

        <Button size="md" fullWidth loading={loading} disabled={loading} onClick={attemptSubmit}>
          {loading ? "Opening checkout…" : `Continue to secure checkout · ${priceDisplay}`}
        </Button>

        <Text fz="xs" ta="center" c="dimmed">
          🔒 Payments by Stripe · One-time, no subscription
        </Text>
      </Stack>
    </Modal>
  );
}
